import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useChecklistStore } from '../store/checklist-store';
import { useChecklistProgress } from '../hooks/use-checklist-progress';
import { OnboardingChecklist } from './OnboardingChecklist';

const SESSION_DISMISS_KEY = 'loki:onboarding-rail-dismissed';
const HIDE_ON_PATH_PREFIX = ['/onboarding', '/help'];

/**
 * Decides whether to render the rail (expanded), a compact icon, or nothing.
 * Per docs/design/onboarding-checklist.md § Mount location + State machine.
 */
export function OnboardingChecklistMount() {
  const location = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  const archived = useChecklistStore((s) => s.archived);
  const { allComplete, isLoading } = useChecklistProgress();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      /* noop */
    }
    setDismissed(true);
  };

  // RBAC: VIEWERs are not provisioning the desk — never render the rail.
  if (role === 'VIEWER') return null;

  // Don't render on routes that own their own chrome.
  if (HIDE_ON_PATH_PREFIX.some((p) => location.pathname.startsWith(p))) return null;

  // Archived → hidden until user clicks "Show setup checklist" elsewhere.
  if (archived) return null;

  // Backend says all done AND user already saw the completion mode → no rail.
  if (allComplete && dismissed) return null;

  if (isLoading) return null;

  if (dismissed) {
    // Compact icon-only rail — clicking restores the expanded rail.
    return (
      <button
        type="button"
        onClick={() => {
          try {
            sessionStorage.removeItem(SESSION_DISMISS_KEY);
          } catch {
            /* noop */
          }
          setDismissed(false);
        }}
        aria-label="Expand setup checklist"
        className="flex h-full w-12 shrink-0 flex-col items-center justify-center gap-2 border-l border-border bg-card text-muted-foreground transition-colors hover:text-amber-500"
      >
        <Sparkles className="h-4 w-4 text-amber-500" />
      </button>
    );
  }

  return <OnboardingChecklist onDismiss={handleDismiss} />;
}
